import { flow, Instance, SnapshotIn, SnapshotOut, types } from "mobx-state-tree";
import { serializeImportQuestionsData, serializeQuestionData } from "./serializers";
import { deserializeQuestion, deserializeQuestions } from "./deserializer";
import { QuestionsApi } from "../../../api/questions/QuestionsApi";
import {
	QuestionCategories,
	QuestionCategoriesMST,
	QuestionTypesMST,
	QuestionTypesRu,
} from "../../../static/enums/Questions";
import { QuestionQuery } from "../../../api/questions/typings";
import { SkillModel } from "../skills/models";
import { TopicModel } from "../topics/models";
import { IQuestionForm, SubquestionForm } from "../../../modules/QuestionForm/QuestionForm";
import { snackbarStore } from "../../ui/SnackbarStore";
import { TexHelperModel } from "../texHelpers/models";
import {DateTime} from "luxon";

export interface ImportQuestionsData {
	dataFilename: string;
	imagesFilename: string;
}

const ChoiceTextSingleConfigModel = types.array(
	types.model({
		label: types.union(types.string, types.number),
	})
);

const ChoiceTextSingleAnswerModel = types.array(types.number);

const InputTableConfigModel = types.array(
	types.model({
		label: types.union(types.string, types.number),
		xpos: types.number,
		ypos: types.number,
	})
);

const MultipleChoiceAnswerModel = types.array(
	types.model({
		answer: types.string,
	})
);

export const QuestionModel = types.model({
	answer_config: types.maybeNull(
		types.union(types.string, InputTableConfigModel, ChoiceTextSingleConfigModel, types.array(types.string))
	),
	children_questions: types.array(
		types.model({
			answer_config: types.maybeNull(
				types.union(types.string, InputTableConfigModel, ChoiceTextSingleConfigModel, types.array(types.string))
			),
			category: types.enumeration(QuestionCategoriesMST),
			correct_answer: types.maybeNull(
				types.union(
					types.string,
					ChoiceTextSingleAnswerModel,
					MultipleChoiceAnswerModel,
					types.array(types.maybeNull(types.union(types.string, types.number)))
				)
			),
			created_at: types.string,
			difficulty: types.number,
			parent_question: types.maybeNull(types.string),
			hint: types.maybeNull(types.string),
			id: types.string,
			number: types.number,
			order: types.number,
			problem: types.string,
			topics: types.array(TopicModel),
			skills: types.array(SkillModel),
			tex_helpers: types.array(TexHelperModel),
			solution: types.maybeNull(types.string),
			title: types.string,
			class: types.enumeration(QuestionTypesMST),
			updated_at: types.string,
		})
	),
	parent_question: types.maybeNull(types.string),
	category: types.enumeration(QuestionCategoriesMST),
	correct_answer: types.maybeNull(
		types.union(
			types.string,
			ChoiceTextSingleAnswerModel,
			MultipleChoiceAnswerModel,
			types.array(types.maybeNull(types.union(types.string, types.number)))
		)
	),
	created_at: types.string,
	difficulty: types.number,
	hint: types.maybeNull(types.string),
	id: types.string,
	number: types.number,
	order: types.number,
	problem: types.string,
	topics: types.array(TopicModel),
	skills: types.array(SkillModel),
	tex_helpers: types.array(TexHelperModel),
	solution: types.maybeNull(types.string),
	title: types.string,
	class: types.enumeration(QuestionTypesMST),
	updated_at: types.string,
});

export const QuestionsModel = types
	.model({
		questions: types.array(QuestionModel),
		page: types.model({
			current_page: types.maybe(types.number),
			from: types.maybe(types.number),
			last_page: types.maybe(types.number),
			per_page: types.maybe(types.number),
			to: types.maybe(types.number),
			total: types.maybe(types.number),
		}),
	})
	.actions((self) => ({
		setQuestions(questions: typeof self.questions) {
			self.questions = questions;
		},
		updateSubquestions: flow(function* (parentId: string, data: SubquestionForm[]) {
			for (let i = 0; i < data.length; i++) {
				if (data[i].id) {
					const { parent_question, ...question } = data[i];
					const request = serializeQuestionData(question as unknown as IQuestionForm);
					yield QuestionsApi.updateQuestion(question.id, request);
				} else {
					data[i].parent_question = { id: parentId };
					const request = serializeQuestionData(data[i] as unknown as IQuestionForm);
					yield QuestionsApi.createQuestion(request);
				}
			}
		}),
	}))
	.actions((self) => {
		const importQuestions = flow(function* (data: ImportQuestionsData) {
			const serializedImportQuestionsData = serializeImportQuestionsData(data);
			const response = yield QuestionsApi.importQuestions(serializedImportQuestionsData);
			self.questions = deserializeQuestions(response);
			snackbarStore.notifySuccess(`Импорт завершен! Всего задач в базе: ${self.questions.length}`);
		});

		const getQuestions = flow(function* (query: QuestionQuery = {}) {
			const response = yield QuestionsApi.getQuestions(query);
			const { page } = response.meta;
			if (page) {
				self.page = page;
			}
			self.questions = deserializeQuestions(response);
		});

		const getQuestion = flow(function* (id: string) {
			const response = yield QuestionsApi.getQuestion(id);
			self.setQuestions(deserializeQuestion(response));
		});

		const createQuestion = flow(function* (data: IQuestionForm, onSuccess: (id: string) => void = (id) => {}) {
			try {
				const request = serializeQuestionData(data);
				const response = yield QuestionsApi.createQuestion(request);
				const question = deserializeQuestion(response);
				yield self.updateSubquestions(question[0].id, data.children_questions);
				self.setQuestions(question);
				snackbarStore.notifySuccess("Готово!");
				onSuccess(self.questions[0].id);
			} catch (e: any) {
				snackbarStore.notifyApiError(e);
			}
		});

		const updateQuestion = flow(function* (data: IQuestionForm) {
			try {
				const request = serializeQuestionData(data);
				const response = yield QuestionsApi.updateQuestion(data.id, request);
				const question = deserializeQuestion(response);
				yield self.updateSubquestions(question[0].id, data.children_questions);
				self.setQuestions(question);
				snackbarStore.notifySuccess("Готово!");
			} catch (e: any) {
				snackbarStore.notifyApiError(e);
			}
		});

		const deleteQuestion = flow(function* (id: string) {
			const data = { id, deleted_at: DateTime.local().toISO({ suppressMilliseconds: false })! } as unknown as IQuestionForm;
			try {
				const request = serializeQuestionData(data);
				yield QuestionsApi.deleteQuestion(id, request);
				snackbarStore.notifySuccess("Вопрос успешно удален!");
			} catch (e: any) {
				snackbarStore.notifyError("Не удалось удалить вопрос! Возможно он используется в системе");
			}
		});

		return {
			importQuestions,
			getQuestions,
			getQuestion,
			updateQuestion,
			createQuestion,
			deleteQuestion
		};
	})
	.views((self) => ({
		get getQuestionsTableData() {
			return self.questions.map(({ id, number, title, class: type, category, order, parent_question }) => ({
				id,
				number,
				title,
				class: QuestionTypesRu[type],
				category: QuestionCategories[category],
				order,
				parent_question,
			}));
		},
		get getQuestionsFullRenderData() {
			return self.questions;
		},
		getQuestionData(id: string) {
			return self.questions.find((question) => question.id === id)!;
		},
		get getQuestionsNumbers() {
			return self.questions.map((question) => question.number);
		},
		get getQuestionsTitles() {
			return self.questions.map((question) => ({ id: question.id, title: question.title }));
		},
	}));

export interface Question extends Instance<typeof QuestionModel> {}

export interface QuestionSnapshotIn extends SnapshotIn<typeof QuestionModel> {}

export interface QuestionSnapshotOut extends SnapshotOut<typeof QuestionModel> {}

export interface Questions extends Instance<typeof QuestionsModel> {}
